import {
  Box,
  Typography,
  Chip,
  Drawer,
  LinearProgress,
  IconButton,
  Card,
  CardContent,
  CircularProgress,
  Icon,
} from "@mui/material";
import { CheckCircle, Close } from "@mui/icons-material";
import { useState } from "react";
import MDButton from "theme/components/MDButton";

export default function SetupDrawer({ items = [] }) {
  const [open, setOpen] = useState(false);

  const requiredItems = items.filter((i) => i.required);
  const completedRequired = requiredItems.filter((i) => i.done).length;

  const progress =
    requiredItems.length === 0 ? 100 : (completedRequired / requiredItems.length) * 100;

  const isComplete = progress === 100;

  return (
    <>
      {/* Card Trigger */}
      <Card
        sx={{
          height: "100%",
          width: "100%",
          p: 2,
          display: "flex",
        }}
      >
        <CardContent
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            gap: 2,
          }}
        >
          {/* Circular Progress */}
          <Box position="relative" display="inline-flex">
            <CircularProgress
              variant="determinate"
              color={isComplete ? "success" : "info"}
              value={progress}
              size={70}
              thickness={4}
            />

            {/* Center Label */}
            <Box
              sx={{
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                position: "absolute",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography fontWeight={700} fontSize={14}>
                {completedRequired}/{requiredItems.length}
              </Typography>
            </Box>
          </Box>

          {/* Status Icon */}
          {/* {isComplete && (
            <Typography fontWeight={700} fontSize={64}>
              <CheckCircle color="success" />
            </Typography>
          )} */}

          {/* Title */}
          <Typography fontWeight={700}>
            {isComplete ? "Setup complete" : "Campaign setup"}
          </Typography>

          {/* Subtitle */}
          <Typography variant="body2" color="text.secondary">
            {isComplete
              ? "Your campaign is ready to go."
              : "Finish setup to unlock full functionality"}
          </Typography>

          {/* CTA */}
          <MDButton
            color="dark"
            variant="contained"
            onClick={() => setOpen(true)}
            startIcon={<Icon>settings</Icon>}
          >
            {isComplete ? "View setup" : "Continue setup"}
          </MDButton>
        </CardContent>
      </Card>

      {/* Drawer */}
      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: "90vW", md: 380, lg: 380},
            maxWidth: "100%",
            height: { xs: "93vh", md: "96vh", lg: "96vh"},
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 2,
            borderBottom: "1px solid #eee",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography fontWeight={700}>Setup your campaign</Typography>
          <IconButton onClick={() => setOpen(false)}>
            <Close />
          </IconButton>
        </Box>

        {/* Progress */}
        <Box sx={{ px: 2, pt: 2 }}>
          <Typography fontWeight={600} mb={1}>
            {completedRequired} of {requiredItems.length} completed
          </Typography>

          <Box
            sx={{
              height: 6,
              borderRadius: 4,
              backgroundColor: "#e0e0e0",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                width: `${progress}%`,
                height: "100%",
                backgroundColor: isComplete ? "success.main" : "info.main",
                transition: "width 0.4s ease",
              }}
            />
          </Box>
        </Box>

        {/* List */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            px: 2,
            pt: 2,
            pb: 2,
          }}
        >
          {items.map((item, index) => {
            const isDone = item.done;

            return (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  gap: 2,
                  alignItems: "flex-start",
                  py: 2,
                  borderBottom: index === items.length - 1 ? "none" : "1px solid #f0f0f0",
                }}
              >
                {/* Number */}
                <Box
                  sx={{
                    minWidth: 32,
                    height: 32,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 700,
                    fontSize: 14,
                    backgroundColor: isDone ? "#e8f5e9" : "#f5f5f5",
                    color: isDone ? "#2e7d32" : "#666",
                    flexShrink: 0,
                  }}
                >
                  {index + 1}
                </Box>

                {/* Content */}
                <Box flex={1} minWidth={0}>
                  <Box display="flex" alignItems="center" gap={1} mb={0.5} flexWrap="wrap">
                    <Typography
                      fontWeight={600}
                      sx={{ whiteSpace: "normal", wordBreak: "break-word" }}
                    >
                      {item.name}
                    </Typography>

                    {!item.required && <Chip label="Optional" size="small" />}
                  </Box>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    mb={1}
                    sx={{ whiteSpace: "normal", wordBreak: "break-word" }}
                  >
                    {item.description}
                  </Typography>

                  {isDone ? (
                    <Chip
                      icon={<CheckCircle />}
                      label="Completed"
                      size="small"
                      color="success"
                      variant="outlined"
                    />
                  ) : item.required ? (
                    <Chip
                      icon={<Icon>priority_high</Icon>}
                      label="Required"
                      size="small"
                      color="warning"
                      variant="outlined"
                    />
                  ) : (
                    <Chip label="Pending" size="small" variant="outlined" />
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Drawer>
    </>
  );
}
